// src/pages/RestaurantReservationsPage.js
// 訂單管理（訂位/外帶）— Owned Reservation System v1 (082 §6.2) + 外帶訂單 (Plan 106)
//
// tabs：今日訂位｜外帶訂單。桌位管理已移出成側欄獨立頁（TablesPage，106 §1.2）。
// 3-column layout（今日訂位 tab）:
//   Layout 左側 nav  |  訂位列表 (中, flex:1)  |  訂位表單 (右側 340px)
// 外帶訂單 tab＝TakeoutOrdersPanel（列表＋右側明細抽屜；Socket 進單走本頁 socket 轉入）
//
// 表單同時負責「新增」與「編輯」(unified mode):
//   - 預設為新增模式
//   - 點任一列 → 進入編輯模式，表單帶入該筆 reservation 的所有欄位
//   - 編輯模式下：列出現藍底 highlight、表單頂部出現橘色 banner、確認鈕變「確認更新」
//   - 「✕ 取消編輯」/「清空」回到新增模式
//
// 附加項目 (addons) 由 companies.plan_settings.reservationAddons 配置驅動，4 種型別：
//   single_select / multi_select / counter / toggle
// 寫入 reservations.extras JSONB；voice flow webhook 同一個 shape 寫入。
// 列表 備註/附加 欄位將 extras 顯示為 chip badges（過濾掉底線開頭的系統 key）。

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Typography,
  TextField,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  IconButton,
  Menu,
  MenuItem,
  Switch,
  Alert,
  Snackbar,
  Tooltip,
  Stack,
  Skeleton,
  InputAdornment,
  Drawer,
  Fab,
  Select,
  Checkbox,
  ListItemText,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import TakeoutDiningIcon from '@mui/icons-material/TakeoutDining';
import PhoneCallbackIcon from '@mui/icons-material/PhoneCallback';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { io } from 'socket.io-client';
import { useSearchParams } from 'react-router-dom';
// P2 inc-2: CallTranscriptDrawer (通話逐字稿) stubbed — it pulls in the P3 voice/call-log
// chain (callLogService + CallTranscriptView). Re-add the 通話紀錄 button + drawer when P3
// voice lands on booking. Web/walk-in reservations have no conversationId anyway.

import Layout, { loadStoredFontScale, FONT_SCALE_EVENT } from '../components/Layout';
import SingleDateNavigator from '../components/dashboard/SingleDateNavigator';
import TakeoutOrdersPanel from '../components/takeout/TakeoutOrdersPanel';
import PendingCallbacksPanel from '../components/PendingCallbacksPanel';
import bookingRequestService from '../services/bookingRequestService';
import takeoutService from '../services/takeoutService';
import restaurantService from '../services/restaurantService';
import DayStatusManager from '../components/DayStatusManager';
import { getPlanSetting } from '../utils/features';
// 顯示規則與營運總覽「新進訂單」共用（119）— 原本是本檔私有函式，逐字搬出去，行為未變。
import {
  todayYmd,
  fmtTime,
  scaleToCardFs,
  fmtDateWithWeekday,
  formatDisplayName,
  STATUS_LABEL,
  SOURCE_LABEL,
  StatusDot,
  SourceChip,
  renderExtraBadges,
  tableChipLabel,
  tableFullLabel,
  HL_BG,
  HL_BAR,
} from '../utils/reservation-display';

// ──────────────────────────────────────────────────────────────
// helpers
// ──────────────────────────────────────────────────────────────

// Legacy: parse "張 小姐" / "張 先生" → { bare, gender }.
// Kept as defensive fallback for rows that haven't been backfilled by migration 109
// (or future write paths that bypass the gender column).
const parseHonorific = (name) => {
  if (!name) return { bare: '', gender: null };
  const s = String(name).trim();
  if (s.endsWith('小姐')) return { bare: s.replace(/\s*小姐$/, '').trim(), gender: 'ms' };
  if (s.endsWith('先生')) return { bare: s.replace(/\s*先生$/, '').trim(), gender: 'mr' };
  return { bare: s, gender: null };
};

const FAR_FUTURE = '2099-12-31';

const StatItem = ({ label, value, color = '#333' }) => (
  <Box sx={{ textAlign: 'center', minWidth: 60 }}>
    <Typography sx={{ fontSize: 22, fontWeight: 700, color, lineHeight: 1.1 }}>{value}</Typography>
    <Typography sx={{ fontSize: 11, color: '#999', mt: 0.5 }}>{label}</Typography>
  </Box>
);

const headerSx = { bgcolor: '#ddd', fontWeight: 'bold', fontSize: '14px', position: 'sticky', top: 0, zIndex: 2, borderBottom: '1px solid #ccc' };
const rowSx = { '&:hover': { bgcolor: '#f0f4ff !important' }, cursor: 'pointer' };
const rowEditingSx = { bgcolor: '#e3f2fd !important', borderLeft: '3px solid #145da0' };
// 119：從推播／營運總覽「新進訂單」跳過來被標出的那一列。
// 不自動淡出 —— 自動消失的話使用者滑一下就再也找不到，而這功能存在的理由就是「找不到」。
const rowHighlightSx = { bgcolor: `${HL_BG} !important`, borderLeft: `4px solid ${HL_BAR}` };
// 字級改由 navbar 全域「A」(標準/大/特大 → fontScale) 驅動；本頁不再有自有的 14/16/18/20 picker。

// 手機/平板卡片基準字級 — 改由全域「A」字級(標準/大/特大)驅動,不再是本頁自己的 picker。
// 沿用先前 per-page 的 20/22/24 視覺(使用者已在 stage 驗過),只是控制點移到 navbar 全域 A。
// 本頁是整頁式 console(m:-3 + calc(100vh)),吃不了 Layout 的 wrapper zoom(會破版),
// 故 <Layout disableContentZoom> + 自行依全域字級縮放內文(卡片與 bottom sheet)。
// MOBILE_CARD_FS / scaleToCardFs 已移到 utils/reservation-display（與新進訂單共用）。

// Split TW phone digits into display segments. Last segment is the "tail"
// (+2px / bold) — quick visual anchor for staff identifying callers.
//   09xxxxxxxx (mobile)         → 0910-951-937   (4-3-3)
//   02xxxxxxxx (Taipei full)    → 02-2765-4321   (2-4-4)
//   0Xxxxxxxxx other 10-digit   → 2-4-4 default
//   0Xxxxxxxxx 9-digit          → 2-3-4
//   xxxxxxxx   local 8-digit    → 2765-4321      (4-4) — Taipei without area code
//   xxxxxxx    local 7-digit    → 123-4567       (3-4) — older landline locals
//   anything else                → return as one segment unchanged
const splitTwPhoneSegments = (digits) => {
  if (digits.length === 10 && digits.startsWith('09')) {
    return [digits.slice(0, 4), digits.slice(4, 7), digits.slice(7)];
  }
  if (digits.length === 10) {
    return [digits.slice(0, 2), digits.slice(2, 6), digits.slice(6)];
  }
  if (digits.length === 9 && digits.startsWith('0')) {
    return [digits.slice(0, 2), digits.slice(2, 5), digits.slice(5)];
  }
  if (digits.length === 8) {
    return [digits.slice(0, 4), digits.slice(4)];
  }
  if (digits.length === 7) {
    return [digits.slice(0, 3), digits.slice(3)];
  }
  return [digits];
};

// Normalize +886 / 886 prefix → 0 so the splitter sees canonical local form.
const normalizePhoneForDisplay = (raw) => {
  let digits = String(raw).replace(/\D/g, '');
  if (digits.startsWith('886')) digits = '0' + digits.slice(3);
  return digits;
};

const renderPhoneWithEmphasis = (phone, baseSize) => {
  if (!phone) return '—';
  const digits = normalizePhoneForDisplay(phone);
  if (!digits) return '—';
  const segments = splitTwPhoneSegments(digits);
  const head = segments.slice(0, -1);
  const tail = segments[segments.length - 1];
  return (
    <>
      {head.map((seg, i) => <React.Fragment key={i}>{seg}-</React.Fragment>)}
      <Box component="span" sx={{ fontWeight: 700, fontSize: baseSize + 2 }}>{tail}</Box>
    </>
  );
};

const Stepper = ({ value, onChange, min = 0, max = 99, labelSize = 22 }) => (
  <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
    <IconButton size="small" onClick={() => onChange(Math.max(min, value - 1))} sx={{ width: 28, height: 28, border: '1px solid #ddd', bgcolor: 'white' }}>
      <Typography sx={{ fontSize: 14, color: '#666' }}>−</Typography>
    </IconButton>
    <Typography sx={{
      fontSize: labelSize, fontWeight: 700, minWidth: 30, textAlign: 'center', color: '#333',
    }}>
      {value}
    </Typography>
    <IconButton size="small" onClick={() => onChange(Math.min(max, value + 1))} sx={{ width: 28, height: 28, border: '1px solid #ddd', bgcolor: 'white' }}>
      <Typography sx={{ fontSize: 14, color: '#666' }}>+</Typography>
    </IconButton>
  </Stack>
);

const AddonChip = ({ label, selected, onClick, fontSize = 14 }) => (
  <Box
    onClick={onClick}
    sx={{
      px: 2, py: 1, borderRadius: 16, border: '1px solid', cursor: 'pointer', userSelect: 'none',
      fontSize, lineHeight: 1.3, minHeight: 40, display: 'inline-flex', alignItems: 'center',
      fontWeight: selected ? 600 : 500,
      borderColor: selected ? '#145da0' : '#b0bac5',
      bgcolor: selected ? '#145da0' : 'white',
      color: selected ? 'white' : '#37474f',
      '&:hover': { borderColor: '#145da0', color: selected ? 'white' : '#145da0' },
      transition: 'all 0.15s',
    }}
  >
    {label}
  </Box>
);

// 手機卡片：大人/小孩拆分（沿用 enterEditMode 的推導：extras._adults/_children，否則 partySize / 0）
const partyBreakdown = (r) => {
  const extras = r.extras || {};
  const adults = Number.isInteger(extras._adults) ? extras._adults : r.partySize;
  const children = Number.isInteger(extras._children) ? extras._children : 0;
  return { adults, children };
};

// ──────────────────────────────────────────────────────────────
// 主元件
// ──────────────────────────────────────────────────────────────

const RestaurantReservationsPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // <md → 手機/小平板：卡片列表 + bottom sheet
  const [sheetOpen, setSheetOpen] = useState(false); // 手機：新增/編輯 bottom sheet 開關

  const [tab, setTab] = useState(0);

  const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' });
  const showSnack = (severity, message) => setSnackbar({ open: true, severity, message });
  const [socketConnected, setSocketConnected] = useState(false);

  const addonConfig = useMemo(() => {
    const v = getPlanSetting('reservationAddons', []);
    return Array.isArray(v) ? v : [];
  }, []);
  // 特殊需求標記（後台於「線上訂位設定」自訂，存在 plan_settings.web_booking.specialRequestTags）
  const specialReqTags = useMemo(() => {
    const w = getPlanSetting('web_booking', {}) || {};
    return Array.isArray(w.specialRequestTags) ? w.specialRequestTags : [];
  }, []);

  // tab 1 state — 點推播帶 ?date=YYYY-MM-DD 進來 → 直接跳到訂位那一天（否則今天）
  const [searchParams] = useSearchParams();
  const qsDate = searchParams.get('date');
  const isValidYmd = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s || '');
  const [date, setDate] = useState(() => (isValidYmd(qsDate) ? qsDate : todayYmd()));
  // app 已開在看板頁時又被推播導到新的 ?date=（同路由只變 query，不會重掛）→ 跟著跳。
  // 手動用日期選擇器換日不會改 URL，故 qsDate 不變、不會被此 effect 蓋回去。
  useEffect(() => {
    if (isValidYmd(qsDate)) setDate(qsDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qsDate]);

  // 119 ?highlight=<訂位id> — 從推播或營運總覽「新進訂單」跳過來時，把那一筆標出來。
  // ⚠️ reservations.id 是 UUID 不是流水號 → 只能驗「非空字串＋長度上限」，
  //    寫成正整數檢查的話每一次 highlight 都會被靜默忽略。
  const qsHighlight = searchParams.get('highlight');
  const isValidResId = (s) => typeof s === 'string' && s.length > 0 && s.length <= 64;
  const [highlightId, setHighlightId] = useState(() => (isValidResId(qsHighlight) ? qsHighlight : null));
  const [highlightMissing, setHighlightMissing] = useState(false); // 那筆已改期/取消 → 提示帶要講話，不要靜默
  const highlightRowRef = useRef(null);
  const highlightScrolledRef = useRef(null); // 已捲過的 id，避免重抓資料後又捲一次

  // URL 換了 highlight（app 開著又被推播導過來）→ 重新標
  useEffect(() => {
    if (isValidResId(qsHighlight)) {
      setHighlightId(qsHighlight);
      setHighlightMissing(false);
      highlightScrolledRef.current = null;
      setTab(0); // 可能停在「待回撥」或「外帶」分頁 → 切回訂位才看得到
    }
  }, [qsHighlight]);

  const clearHighlight = useCallback(() => {
    setHighlightId(null);
    setHighlightMissing(false);
  }, []);

  const [reservations, setReservations] = useState([]);
  const [stats, setStats] = useState({ total: 0, confirmed: 0, seated: 0, cancelled: 0, noShow: 0, totalGuests: 0 });
  const [resLoading, setResLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  // 全域字級(navbar「A」標準/大/特大)— 本頁整頁式 console 自行依此縮放內文（桌機+手機統一，
  // 不再有本頁自有的 14/16/18/20 picker）。監聽 Layout 廣播即時同步。
  const [fontScale, setFontScale] = useState(() => loadStoredFontScale());
  useEffect(() => {
    const onScale = (e) => setFontScale(typeof e?.detail === 'number' ? e.detail : loadStoredFontScale());
    window.addEventListener(FONT_SCALE_EVENT, onScale);
    return () => window.removeEventListener(FONT_SCALE_EVENT, onScale);
  }, []);
  const mobileFs = scaleToCardFs(fontScale); // 手機卡片基準字級(20/22/24)
  // 桌機表格 cell 字級：由全域字級驅動（標準16 / 大~19 / 特大~22）。
  const cellFontSize = Math.round(16 * fontScale);
  const cellSx = useMemo(() => ({ py: 1.75, fontSize: cellFontSize }), [cellFontSize]);
  // 內文/表單字級縮放：手機依卡片基準(mobileFs/15)、桌機依全域字級(fontScale)，一起縮放。
  const formFz = (base) => Math.round(base * (isMobile ? mobileFs / 15 : fontScale));
  // 頂部導覽列(日期/客滿)字級：手機不放大(維持 base、才塞得進一排)；桌機跟全域「A」。
  const headerFz = (base) => Math.round(base * (isMobile ? 1 : fontScale));
  const businessHours = useMemo(() => {
    const v = getPlanSetting('businessHours', []);
    return Array.isArray(v) ? v : [];
  }, []);
  const validateTimeInWindows = useCallback((time) => {
    if (businessHours.length === 0) return null;
    const t = String(time).slice(0, 5);
    const ok = businessHours.some(w =>
      w && typeof w.start === 'string' && typeof w.end === 'string' &&
      t >= w.start && t <= w.end
    );
    if (ok) return null;
    const desc = businessHours
      .map(w => `${w.name || ''} ${w.start}-${w.end}`.trim())
      .join('，');
    return `不在營業時段（${desc}），請選擇其他時間`;
  }, [businessHours]);
  const [rowMenuAnchor, setRowMenuAnchor] = useState(null);
  const [rowMenuTarget, setRowMenuTarget] = useState(null);

  // tab 1 state — 待回撥訂位（feature_plans/115；資料在 PendingCallbacksPanel 自管）
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingRefresh, setPendingRefresh] = useState(0);
  const [pendingRequest, setPendingRequest] = useState(null);  // 目前帶入表單的那一筆

  // tab 2 state — 外帶訂單（Plan 106；資料在 TakeoutOrdersPanel 自管）
  const [takeoutCounts, setTakeoutCounts] = useState({ reported: 0, confirmed: 0, ready: 0, picked_up: 0, cancelled: 0 });
  const [takeoutRefresh, setTakeoutRefresh] = useState(0); // Socket 進單 → 遞增觸發面板重抓

  // ──────── form pane state ────────
  // editingReservation: null = create mode; reservation object = edit mode
  const [editingReservation, setEditingReservation] = useState(null);
  // P2 inc-2: callDrawerConvId state removed with the 通話 drawer stub (re-add with P3 voice).
  const buildEmptyForm = useCallback(() => ({
    reservationTime: '18:00',
    adults: 0,
    children: 0,
    customerName: '',
    customerGender: 'ms',
    customerPhone: '',
    customerNote: '',
    extras: {},
    tables: [],  // 118 併桌：已選桌 [{id,tableNumber,seats}]（空 → 自動配桌；1 張=單桌；≥2=併桌）
  }), []);
  const [form, setForm] = useState(buildEmptyForm);
  const [submitting, setSubmitting] = useState(false);
  // T-023 自選桌號 picker
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerTables, setPickerTables] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);

  const setField = (key, value) => setForm(f => ({ ...f, [key]: value }));
  const setExtra = (key, value) => setForm(f => ({ ...f, extras: { ...f.extras, [key]: value } }));

  // 特殊需求（多選）— 與線上訂位頁一致，存成 extras.specialRequests（以「、」join 的字串）。
  const selectedSpecialReqs = useMemo(() => {
    const v = form.extras?.specialRequests;
    return Array.isArray(v) ? v : (typeof v === 'string' && v ? v.split('、') : []);
  }, [form.extras]);
  const toggleSpecialReq = (tag) => {
    const next = selectedSpecialReqs.includes(tag)
      ? selectedSpecialReqs.filter(t => t !== tag)
      : [...selectedSpecialReqs, tag];
    setExtra('specialRequests', next.length ? next.join('、') : undefined);
  };
  const setSpecialReqs = (arr) => setExtra('specialRequests', arr.length ? arr.join('、') : undefined);

  // 24h time input — handles "1830" → "18:30"
  const handleTimeInput = (raw) => {
    const v = String(raw).replace(/[^0-9]/g, '').slice(0, 4);
    if (v.length === 0) setField('reservationTime', '');
    else if (v.length <= 2) setField('reservationTime', v);
    else setField('reservationTime', v.slice(0, 2) + ':' + v.slice(2));
  };

  const enterEditMode = useCallback((r) => {
    // 119：使用者已經自己點到那一列了 → 黃底功成身退（否則會和編輯藍底疊在一起）
    setHighlightId(null);
    setHighlightMissing(false);
    // Prefer canonical gender column (migration 109). For legacy rows still
    // carrying the honorific suffix on customer_name, parseHonorific recovers it.
    const parsed = parseHonorific(r.customerName);
    const gender = r.customerGender || parsed.gender || 'mr';
    const bare = parsed.bare; // strips suffix if present, else returns name as-is
    const extras = r.extras || {};
    const a = Number.isInteger(extras._adults) ? extras._adults : r.partySize;
    const c = Number.isInteger(extras._children) ? extras._children : 0;
    setEditingReservation(r);
    setForm({
      reservationTime: fmtTime(r.reservationTime),
      adults: a,
      children: c,
      customerName: bare,
      customerGender: gender,
      customerPhone: r.customerPhone || '',
      customerNote: r.customerNote || '',
      extras: { ...extras }, // includes _adults/_children; filtered at submit
      tables: (Array.isArray(r.combinedTables) && r.combinedTables.length)
        ? r.combinedTables.map((t) => ({ id: t.id, tableNumber: t.tableNumber, seats: t.seats }))
        : (r.tableId ? [{ id: r.tableId, tableNumber: r.table?.tableNumber, seats: r.table?.seats }] : []),
    });
  }, []);

  const exitEditMode = useCallback(() => {
    setEditingReservation(null);
    setForm(buildEmptyForm());
    // 表單回到空白＝這次不再是從待回撥帶入的。不清掉的話，「選了一筆待回撥
    // → 直接按新增 → 建另一筆訂位」會把那筆待回撥標成已成交、綁上錯的
    // reservationId，來源也會被記成 voice_callback
    setPendingRequest(null);
  }, [buildEmptyForm]);

  // 手機 bottom sheet：新增（清空表單並開）/ 編輯（帶入並開）/ 關閉（重置並收）。
  // handleFormClose 桌機/手機共用：桌機時 setSheetOpen(false) 是 no-op，行為＝原「清空/取消編輯」。
  const openNewSheet = useCallback(() => { exitEditMode(); setSheetOpen(true); }, [exitEditMode]);
  const openEditSheet = useCallback((r) => { enterEditMode(r); setSheetOpen(true); }, [enterEditMode]);
  const handleFormClose = useCallback(() => {
    exitEditMode();
    setSheetOpen(false);
    setPendingRequest(null);   // 待回撥：關掉表單＝放棄這次帶入，回到清單
  }, [exitEditMode]);

  // 選一筆待回撥 → 把「既有」訂位表單預先填好。
  // 刻意不重造表單：自選桌號（含佔用狀態、自動配桌、seatsEnough 併桌驗證）、
  // 預定料理、特殊需求、稱謂全部沿用，14 位送出時也會撞到既有的併桌守門。
  // 表單本身不帶日期（日期在頁面層），所以要一併把日期切到那天。
  const selectPendingRequest = useCallback((r) => {
    setEditingReservation(null);
    setPendingRequest(r);
    if (r.reservationDate) setDate(r.reservationDate);
    setForm({
      ...buildEmptyForm(),
      reservationTime: fmtTime(r.reservationTime) || '18:00',
      adults: r.adults || 0,
      children: r.children || 0,
      customerName: r.customerName || '',
      customerGender: r.customerGender === 'mr' ? 'mr' : 'ms',
      customerPhone: r.customerPhone || '',
    });
    setSheetOpen(true);   // 手機開 bottom sheet；桌機為 no-op（面板由 pendingRequest 控制展開）
  }, [buildEmptyForm]);

  // badge 的數字要在「還沒點進待回撥分頁」的時候就正確——那正是這個功能存在的理由。
  // PendingCallbacksPanel 只有 tab===1 才掛載，只靠它回報的話，老闆一天打開一次
  // 頁面看到的永遠是 0，等於回到通話紀錄被忽略的老問題。所以在頁面層自己抓一次。
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const body = await bookingRequestService.list({ status: 'open' });
        if (alive) setPendingCount(body?.openCount ?? (body?.data?.length || 0));
      } catch (e) {
        // 拿不到就維持原值，不擋頁面——badge 不是關鍵路徑
        console.error('讀取待回撥數量失敗:', e);
      }
    })();
    return () => { alive = false; };
  }, [pendingRefresh]);

  // 外帶 badge 同理 —— TakeoutOrdersPanel 只在 tab===2 掛載，
  // 只靠它回報的話「待核帳」的數字要點進去才會出現。
  // 日期取今天，與面板的預設一致；面板開著時它自己回報的數字會蓋過這裡。
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const body = await takeoutService.getOrders({ date: todayYmd() });
        if (alive && body?.success) setTakeoutCounts(body.counts || {});
      } catch (e) {
        console.error('讀取外帶訂單數量失敗:', e);
      }
    })();
    return () => { alive = false; };
  }, [takeoutRefresh]);

  // 非成交結案（聯絡不上／已聯絡未成交／不需處理）
  const resolvePending = useCallback(async (outcome) => {
    if (!pendingRequest) return;
    try {
      const body = await bookingRequestService.resolve(pendingRequest.id, { outcome });
      if (body?.success) {
        showSnack('success', outcome === 'no_answer' ? '已標記聯絡不上（仍留在清單）' : '已結案');
        setPendingRequest(null);
        exitEditMode();
        setSheetOpen(false);
        setPendingRefresh((n) => n + 1);
      } else {
        showSnack('error', body?.message || '結案失敗');
      }
    } catch (e) {
      showSnack('error', '結案失敗');
    }
  }, [pendingRequest, exitEditMode]);

  // when admin clicks a different date above the table, drop edit mode
  // (the reservation being edited is for a different day)
  useEffect(() => {
    if (editingReservation && editingReservation.reservationDate !== date) {
      exitEditMode();
    }
  }, [date, editingReservation, exitEditMode]);

  const socketRef = useRef(null);
  const companyId = useMemo(() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      return u.companyId || u.company_id || null;
    } catch (e) {
      return null;
    }
  }, []);

  const fetchReservations = useCallback(async (targetDate) => {
    setResLoading(true);
    try {
      const body = await restaurantService.getReservations({ date: targetDate });
      if (body?.success && body.data) {
        setReservations(body.data.reservations || []);
        setStats(body.data.stats || { total: 0, confirmed: 0, seated: 0, cancelled: 0, noShow: 0, totalGuests: 0 });
      } else {
        showSnack('error', body?.error || '取得訂位列表失敗');
      }
    } catch (e) {
      showSnack('error', '取得訂位列表失敗');
    } finally {
      setResLoading(false);
    }
  }, []);

  useEffect(() => { fetchReservations(date); }, [date, fetchReservations]);

  // 119 highlight：資料到齊後才判斷「找不找得到那一筆」並捲過去。
  // 只在 loading 結束後判斷，否則載入中的空陣列會誤判成「已改期/取消」。
  useEffect(() => {
    if (!highlightId || resLoading) return;
    const hit = reservations.some((r) => String(r.id) === String(highlightId));
    setHighlightMissing(!hit);
    if (!hit) return;
    if (highlightScrolledRef.current === highlightId) return; // 同一筆只捲一次
    highlightScrolledRef.current = highlightId;
    // ref 要等該列 render 完才有值
    const id = window.setTimeout(() => {
      const el = highlightRowRef.current;
      if (!el) return;
      const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
    }, 120);
    return () => window.clearTimeout(id);
  }, [highlightId, reservations, resLoading]);

  // socket
  useEffect(() => {
    if (!companyId) return undefined;
    const socketUrl = process.env.REACT_APP_SOCKET_URL
      || process.env.REACT_APP_API_URL?.replace('/api', '')
      || window.location.origin;
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    try {
      const socket = io(socketUrl, {
        auth: { token },
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 2000,
      });
      socket.on('connect', () => {
        setSocketConnected(true);
        socket.emit('join_company', { companyId, userId: localStorage.getItem('userId') || 'anonymous' });
      });
      socket.on('disconnect', () => setSocketConnected(false));
      socket.on('reconnect', () => {
        setSocketConnected(true);
        socket.emit('join_company', { companyId, userId: localStorage.getItem('userId') || 'anonymous' });
      });
      socketRef.current = socket;
    } catch (e) { /* socket failure does not block */ }
    return () => {
      if (socketRef.current) { socketRef.current.disconnect(); socketRef.current = null; }
    };
  }, [companyId]);

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return undefined;
    const handler = (payload) => {
      const r = payload?.reservation;
      if (!r) return;
      if (r.reservationDate !== date) return;
      fetchReservations(date);
      const tableLabel = payload?.assignment?.table?.tableNumber || '候補';
      showSnack('info', `新訂位：${r.customerName} ${fmtTime(r.reservationTime)} (${tableLabel})`);
    };
    socket.on('reservation.created', handler);
    return () => { socket.off('reservation.created', handler); };
  }, [date, fetchReservations]);

  // 外帶訂單即時進單/異動（Plan 106）— 遞增 refreshSignal 讓 TakeoutOrdersPanel 重抓
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return undefined;
    const onCreated = (payload) => {
      setTakeoutRefresh((n) => n + 1);
      const o = payload?.order;
      if (o) showSnack('info', `新外帶訂單：${o.orderNo} ${o.customerName} ${fmtTime(o.pickupTime)} 取餐`);
    };
    const onUpdated = () => setTakeoutRefresh((n) => n + 1);
    socket.on('takeout.order.created', onCreated);
    socket.on('takeout.order.updated', onUpdated);
    return () => {
      socket.off('takeout.order.created', onCreated);
      socket.off('takeout.order.updated', onUpdated);
    };
  }, [companyId]);

  // row actions
  const openRowMenu = (e, reservation) => {
    e.stopPropagation();
    setRowMenuAnchor(e.currentTarget);
    setRowMenuTarget(reservation);
  };
  const closeRowMenu = () => { setRowMenuAnchor(null); setRowMenuTarget(null); };
  const handleStatusChange = async (status) => {
    const target = rowMenuTarget; closeRowMenu();
    if (!target) return;
    const body = await restaurantService.updateReservationStatus(target.id, status);
    if (body?.success) { showSnack('success', `已更新狀態：${STATUS_LABEL[status] || status}`); fetchReservations(date); }
    else { showSnack('error', body?.error || '更新狀態失敗'); }
  };
  const handleCancel = async () => {
    const target = rowMenuTarget; closeRowMenu();
    if (!target) return;
    if (!window.confirm(`確定取消 ${target.customerName} 的訂位嗎？`)) return;
    const body = await restaurantService.cancelReservation(target.id);
    if (body?.success) {
      showSnack('success', '已取消訂位');
      if (editingReservation && editingReservation.id === target.id) exitEditMode();
      fetchReservations(date);
    }
    else { showSnack('error', body?.error || '取消失敗'); }
  };
  const openEditFromMenu = () => {
    const target = rowMenuTarget; closeRowMenu();
    if (target) openEditSheet(target); // 手機：開 bottom sheet；桌機：setSheetOpen 無副作用（Drawer 未渲染）
  };

  // ──────── form submit (create or update) ────────
  const totalParty = (parseInt(form.adults, 10) || 0) + (parseInt(form.children, 10) || 0);
  const isEditing = !!editingReservation;

  // ──────── T-023 自選桌號 picker ────────
  const openTablePicker = async () => {
    if (!form.reservationTime || !/^\d{2}:\d{2}$/.test(form.reservationTime)) {
      showSnack('error', '請先填時間再選桌'); return;
    }
    if (totalParty < 1) { showSnack('error', '請先填人數再選桌'); return; }
    setPickerOpen(true);
    setPickerLoading(true);
    const body = await restaurantService.getTableAvailability({
      date,
      time: form.reservationTime,
      partySize: totalParty,
      ...(editingReservation ? { excludeReservationId: editingReservation.id } : {}),
    });
    setPickerTables(body?.data?.tables || []);
    setPickerLoading(false);
  };
  // 多選桌併桌：toggle 加入/移除，不關閉抽屜（可繼續選）
  const toggleTable = (t) => setForm(f => {
    const has = (f.tables || []).some((x) => x.id === t.id);
    const tables = has
      ? f.tables.filter((x) => x.id !== t.id)
      : [...(f.tables || []), { id: t.id, tableNumber: t.tableNumber, seats: t.seats }];
    return { ...f, tables };
  });
  const pickAutoAssign = () => { setForm(f => ({ ...f, tables: [] })); setPickerOpen(false); };
  const selectedTables = form.tables || [];
  const selectedSeats = selectedTables.reduce((s, t) => s + (t.seats || 0), 0);
  const seatsEnough = selectedTables.length === 0 || selectedSeats >= totalParty; // 空=自動配桌;有選則需夠

  // 依適配排序：可選(開放且未佔)在前、按多出的座位小→大（剛好最前）；不可選(已佔/未開放)灰在後。
  const pickerRows = useMemo(() => {
    const selectedIds = new Set((form.tables || []).map((x) => x.id));
    const rows = pickerTables.map((t) => {
      // 桌位管理已關閉「開放配桌」(isAvailable=false) → 手動選桌不可選、灰掉標「未開放配桌」。
      // 例外：該桌已配在此單（編輯舊訂位、桌事後被關）→ 仍可操作，避免它從清單消失、無法取消。
      const alreadySelected = selectedIds.has(t.id);
      const closed = t.isAvailable === false && !alreadySelected;
      const selectable = !t.occupied && !closed; // 併桌：個別容量不限，可選任何未佔且開放的桌（靠總座位把關）
      const fit = t.occupied
        ? { text: t.occupiedBy ? `已被佔用（${t.occupiedBy}）` : '已被佔用', tone: 'occ' }
        : (closed ? { text: '未開放配桌', tone: 'closed' } : { text: '', tone: 'ok' });
      return { ...t, selectable, fit };
    });
    const num = (x) => parseInt(x.tableNumber, 10) || 0;
    rows.sort((a, b) => {
      if (a.selectable !== b.selectable) return a.selectable ? -1 : 1;
      return num(a) - num(b);
    });
    return rows;
  }, [pickerTables, form.tables]);

  const submitForm = async () => {
    if (!form.customerName.trim()) { showSnack('error', '請輸入姓名'); return; }
    if (form.adults < 1) { showSnack('error', '請輸入大人人數 (>= 1)'); return; }
    if (!form.reservationTime || !/^\d{2}:\d{2}$/.test(form.reservationTime)) {
      showSnack('error', '請輸入正確的時間 (HH:MM)'); return;
    }
    const hoursErr = validateTimeInWindows(form.reservationTime);
    if (hoursErr) { showSnack('error', hoursErr); return; }

    // 自動配桌 + 大團超過最大單桌 → 擋下並提示併桌（避免靜默變候補）。建單時檢查；編輯不擋。
    if (!isEditing && selectedTables.length === 0) {
      const av = await restaurantService.getTableAvailability({ date, time: form.reservationTime, partySize: totalParty });
      const tbls = av?.data?.tables || [];
      // 只算「開放配桌」的桌：自動配桌只會用開放桌，關閉的大桌不能算進最大單桌（否則守門誤判、團靜默變候補）。
      const maxSeats = tbls.filter((t) => t.isAvailable !== false).reduce((m, t) => Math.max(m, t.seats || 0), 0);
      if (tbls.length && totalParty > maxSeats) {
        showSnack('error', `本團 ${totalParty} 位超過最大單桌（${maxSeats} 位），請用「自選桌號」選多張桌併桌`);
        setPickerTables(tbls);
        setPickerOpen(true);
        return;
      }
    }

    // Bare name + separate gender — backend writes both columns.
    const bareName = form.customerName.trim();
    const gender = form.customerGender === 'mr' ? 'mr' : 'ms';

    setSubmitting(true);
    let body;
    if (isEditing) {
      body = await restaurantService.updateReservation(editingReservation.id, {
        reservationTime: form.reservationTime,
        partySize: totalParty,
        customerName: bareName,
        customerGender: gender,
        customerPhone: form.customerPhone?.trim() || null,
        customerNote: form.customerNote?.trim() || null,
        extras: form.extras,
        adults: form.adults,
        children: form.children,
      });
      // 桌位有變更 → reassign（updateReservation 不動桌位）；陣列 → 併桌 reassign
      const newIds = selectedTables.map((t) => t.id).sort((a, b) => a - b);
      const oldIds = ((Array.isArray(editingReservation.combinedTables) && editingReservation.combinedTables.length)
        ? editingReservation.combinedTables.map((t) => t.id)
        : (editingReservation.tableId ? [editingReservation.tableId] : [])).sort((a, b) => a - b);
      if (body?.success && JSON.stringify(newIds) !== JSON.stringify(oldIds)) {
        const rb = await restaurantService.reassignTable(editingReservation.id, newIds);
        if (!rb?.success) {
          setSubmitting(false);
          showSnack('error', rb?.error || '更換桌位失敗');
          return;
        }
      }
    } else {
      body = await restaurantService.createReservation({
        reservationDate: date,
        reservationTime: form.reservationTime,
        partySize: totalParty,
        customerName: bareName,
        customerGender: gender,
        customerPhone: form.customerPhone?.trim() || null,
        customerNote: form.customerNote?.trim() || null,
        // 從待回撥帶入的訂位源自 AI 語音來電，記成 manual 會讓語音成效被低估
        // （這類是超過人數上限的團體，金額通常不小）
        source: pendingRequest ? 'voice_callback' : 'manual',
        extras: form.extras,
        adults: form.adults,
        children: form.children,
        tableIds: selectedTables.map((t) => t.id), // 118 併桌：多選桌（空 → 自動配桌；1=單桌；≥2=併桌）
      });
    }
    setSubmitting(false);

    if (body?.success && body.data) {
      const tableLabel = body.data.assignment?.table?.tableNumber || '';
      const msg = isEditing
        ? '已更新訂位'
        : `已建立訂位${tableLabel ? `（${tableLabel}）` : ''}`;
      showSnack('success', msg);
      // 待回撥：訂位真的建起來了才結案，並把 reservationId 綁回去（feature_plans/115）
      if (!isEditing && pendingRequest) {
        const reservationId = body.data.id || body.data.reservation?.id || null;
        try {
          await bookingRequestService.resolve(pendingRequest.id, { outcome: 'booked', reservationId });
        } catch (e) {
          console.error('待回撥結案失敗（訂位已建立）:', e);
        }
        setPendingRequest(null);
        setPendingRefresh((n) => n + 1);
      }
      exitEditMode();
      setSheetOpen(false); // 手機：送出成功收起 bottom sheet（桌機為 no-op）
      fetchReservations(date);
    } else {
      showSnack('error', body?.error || (isEditing ? '更新訂位失敗' : '建立訂位失敗'));
    }
  };


  const filteredReservations = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return reservations;
    return reservations.filter(r => {
      const haystack = [
        r.customerName || '',
        r.customerPhone || '',
        r.customerNote || '',
      ].join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [reservations, searchQuery]);

  // Interleave hour-group divider rows between bookings, mirroring inline.app's
  // visual grouping. Each divider shows the hour + per-hour rollup (guests, groups).
  const groupedRows = useMemo(() => {
    if (!filteredReservations.length) return [];
    const hourStats = {};
    for (const r of filteredReservations) {
      const h = String(r.reservationTime || '').slice(0, 2);
      if (!hourStats[h]) hourStats[h] = { count: 0, totalGuests: 0 };
      hourStats[h].count += 1;
      hourStats[h].totalGuests += r.partySize || 0;
    }
    const out = [];
    let currentHour = null;
    for (const r of filteredReservations) {
      const h = String(r.reservationTime || '').slice(0, 2);
      if (h !== currentHour) {
        out.push({ type: 'divider', hour: h, count: hourStats[h].count, totalGuests: hourStats[h].totalGuests });
        currentHour = h;
      }
      out.push({ type: 'booking', reservation: r });
    }
    return out;
  }, [filteredReservations]);

  const renderSkeletonRows = (colCount, count = 8) => (
    Array.from({ length: count }).map((_, i) => (
      <TableRow key={`sk-${i}`}>
        {Array.from({ length: colCount }).map((__, j) => (
          <TableCell key={j} sx={cellSx}><Skeleton variant="text" /></TableCell>
        ))}
      </TableRow>
    ))
  );

  const renderTabStats = () => {
    if (tab === 0) {
      return (
        <>
          <StatItem label="總筆數" value={stats.total} />
          <StatItem label="已確認" value={stats.confirmed} color="#145da0" />
          <StatItem label="用餐中" value={stats.seated} color="#4caf50" />
          <StatItem label="已取消" value={stats.cancelled} color="#9e9e9e" />
          <StatItem label="總人數" value={stats.totalGuests} color="#e65100" />
        </>
      );
    }
    return (
      <>
        <StatItem label="待核帳" value={takeoutCounts.reported || 0} color="#b45309" />
        <StatItem label="準備中" value={takeoutCounts.confirmed || 0} color="#145da0" />
        <StatItem label="可取餐" value={takeoutCounts.ready || 0} color="#2e7d32" />
        <StatItem label="已取餐" value={takeoutCounts.picked_up || 0} color="#9e9e9e" />
      </>
    );
  };

  // 外帶 tab badge＝未完成筆數（待核帳＋準備中＋可取餐）
  const takeoutActive = (takeoutCounts.reported || 0) + (takeoutCounts.confirmed || 0) + (takeoutCounts.ready || 0);

  // form's displayed date = the date currently shown above the table
  const formDateDisplay = useMemo(() => {
    try {
      const d = new Date(date + 'T00:00:00');
      const wkmap = ['日','一','二','三','四','五','六'];
      return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日 星期${wkmap[d.getDay()]}`;
    } catch { return date; }
  }, [date]);

  const renderAddonControl = (cfg) => {
    const val = form.extras[cfg.key];
    if (cfg.type === 'single_select') {
      return (
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          {(cfg.options || []).map(opt => (
            <AddonChip
              key={opt.value}
              label={opt.label}
              selected={val === opt.value}
              fontSize={formFz(14)}
              onClick={() => setExtra(cfg.key, val === opt.value ? undefined : opt.value)}
            />
          ))}
        </Stack>
      );
    }
    if (cfg.type === 'multi_select') {
      // 既有訂位的 extras 值可能是後端 sanitizeAddons join 過的字串（如「川味牛四寶、首烏燉全雞」），
      // 也可能是本次編輯產生的陣列 → 兩者都要還原成陣列，chips 才會依既有選取正確勾起。
      const selected = Array.isArray(val)
        ? val
        : (typeof val === 'string' && val ? val.split('、') : []);
      // 手機：改成下拉選單（可多選），省空間；桌機維持 chips。
      if (isMobile) {
        return (
          <Select
            multiple fullWidth size="small" displayEmpty
            value={selected}
            onChange={(e) => {
              const v = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
              setExtra(cfg.key, v.length > 0 ? v : undefined);
            }}
            renderValue={(sel) => (!sel || sel.length === 0)
              ? <span style={{ color: '#999' }}>點此選擇（可多選）</span>
              : (cfg.options || []).filter(o => sel.includes(o.value)).map(o => o.label).join('、')}
            sx={{ '& .MuiSelect-select': { fontSize: formFz(14), py: 1, whiteSpace: 'normal' } }}
            MenuProps={{ PaperProps: { sx: { maxHeight: 340 } } }}
          >
            {(cfg.options || []).map(opt => (
              <MenuItem key={opt.value} value={opt.value} sx={{ py: 0.25 }}>
                <Checkbox checked={selected.includes(opt.value)} size="small" sx={{ py: 0.25 }} />
                <ListItemText primary={opt.label} primaryTypographyProps={{ fontSize: formFz(14) }} />
              </MenuItem>
            ))}
          </Select>
        );
      }
      return (
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          {(cfg.options || []).map(opt => {
            const isOn = selected.includes(opt.value);
            return (
              <AddonChip
                key={opt.value}
                label={opt.label}
                selected={isOn}
                fontSize={formFz(14)}
                onClick={() => {
                  const next = isOn ? selected.filter(v => v !== opt.value) : [...selected, opt.value];
                  setExtra(cfg.key, next.length > 0 ? next : undefined);
                }}
              />
            );
          })}
        </Stack>
      );
    }
    if (cfg.type === 'counter') {
      const num = Number.isInteger(val) ? val : 0;
      return (
        <Stepper
          value={num}
          min={0}
          max={cfg.max || 10}
          labelSize={16}
          onChange={(v) => setExtra(cfg.key, v > 0 ? v : undefined)}
        />
      );
    }
    if (cfg.type === 'toggle') {
      return (
        <Switch
          size="small"
          checked={val === true}
          onChange={(e) => setExtra(cfg.key, e.target.checked ? true : undefined)}
        />
      );
    }
    return null;
  };

  // ──────── 訂位表單內容（桌機右欄 340px + 手機 bottom sheet 共用）────────
  const renderFormPaneInner = () => (
    <>
      <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
        <Box>
          <Typography sx={{ fontSize: formFz(15), fontWeight: 700 }}>
            {isEditing ? '編輯訂位' : '新增訂位'}
          </Typography>
          <Typography sx={{ fontSize: formFz(11), color: '#999' }}>
            {isEditing ? `${editingReservation.customerName} · ${fmtTime(editingReservation.reservationTime)}` : '直接填寫，不需點按開啟'}
          </Typography>
        </Box>
        <Tooltip title={isEditing ? '取消編輯' : (isMobile ? '關閉' : '清空')}>
          <IconButton size={isMobile ? 'medium' : 'small'} onClick={handleFormClose}>
            <ClearIcon fontSize={isMobile ? 'medium' : 'small'} sx={{ color: '#999' }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Edit-mode banner */}
      {isEditing && (
        <Box sx={{
          bgcolor: '#fff3e0', borderBottom: '1px solid #ffb74d',
          px: 1.5, py: 0.75, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          fontSize: formFz(12), color: '#e65100', flexShrink: 0,
        }}>
          <Box>編輯訂位中 — <b>{editingReservation.customerName}</b></Box>
          <Button size="small" onClick={handleFormClose}
            sx={{ minWidth: 'auto', fontSize: formFz(11), color: '#145da0', textTransform: 'none' }}>
            ✕ 取消編輯
          </Button>
        </Box>
      )}

      {/* 待回撥橫幅（feature_plans/115）— 與編輯橫幅共用同一個槽與配色。
          只放三件事：為什麼要你確認／一鍵撥號／客人原話。 */}
      {pendingRequest && (
        <Box sx={{
          bgcolor: '#fff3e0', borderBottom: '1px solid #ffb74d',
          px: 1.5, py: 1, fontSize: formFz(12), color: '#e65100', flexShrink: 0,
        }}>
          {/* 標題列不放關閉鈕——上方「新增訂位」那排本來就有 ✕，兩顆會讓人猶豫按哪顆 */}
          <Box sx={{ minWidth: 0 }}>
            <b>AI 收下的訂位需求</b> — {pendingRequest.reasonText}
          </Box>

          {/* 撥號是老闆在這個畫面的主要動作（下一步是真的拿起電話），給它主按鈕的份量。
              號碼放大並拉開字距，方便照著念／核對。 */}
          <Button
            fullWidth variant="contained" href={`tel:${pendingRequest.customerPhone}`}
            sx={{
              mt: 1, bgcolor: '#e65100', '&:hover': { bgcolor: '#bf360c' },
              textTransform: 'none', fontWeight: 800, py: isMobile ? 1.5 : 1.15,
              fontSize: formFz(17), lineHeight: 1.2,
              boxShadow: '0 2px 6px rgba(230,81,0,.28)',
            }}
          >
            📞 撥給 {pendingRequest.customerName || ''}
            <Box component="span" sx={{ ml: 1, letterSpacing: '.06em', fontVariantNumeric: 'tabular-nums' }}>
              {pendingRequest.customerPhone}
            </Box>
          </Button>
          {pendingRequest.reasonDetail && (
            <Typography sx={{ fontSize: formFz(11), color: '#a1887f', mt: 0.5 }}>
              {pendingRequest.reasonDetail}
            </Typography>
          )}
          {pendingRequest.callLog?.excerpt?.length > 0 && (
            <Box component="details" sx={{ mt: 0.75 }}>
              <Box component="summary" sx={{ cursor: 'pointer', fontSize: formFz(11), color: '#8d6e63' }}>
                客人原話（{pendingRequest.callLog.excerpt.length} 句）
              </Box>
              <Box sx={{ mt: 0.5, pl: 1, borderLeft: '2px solid #ffcc80' }}>
                {pendingRequest.callLog.excerpt.map((t, i) => (
                  <Typography key={i} sx={{ fontSize: formFz(11.5), color: '#5d4037', mb: 0.25 }}>
                    「{t.text}」
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}

      <Box sx={{
        flex: 1, overflow: 'auto', px: 2, py: 2,
        '&::-webkit-scrollbar': { width: '6px' },
        '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.2)', borderRadius: '3px' },
      }}>
        {/* Date (display only) + 24h time input */}
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography sx={{ fontSize: isMobile ? 24 : 20, fontWeight: 600, color: '#444', mb: 1 }}>{formDateDisplay}</Typography>
          <TextField
            type="text"
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-2][0-9]:[0-5][0-9]',
              maxLength: 5,
              style: { fontSize: 36, fontWeight: 700, textAlign: 'center', padding: '4px 0', letterSpacing: 1 },
            }}
            value={form.reservationTime}
            onChange={(e) => handleTimeInput(e.target.value)}
            placeholder="HH:MM"
            variant="standard"
            sx={{ width: 130,
              '& .MuiInput-underline:before': { borderBottom: '1px dashed transparent' },
              '& .MuiInput-underline:after': { borderBottom: '1px solid #145da0' },
              '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottom: '1px dashed #ccc' },
            }}
          />
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontSize: formFz(12), mb: 1 }}>大人 <span style={{ color: '#d32f2f' }}>*</span></Typography>
            <Stepper value={form.adults} onChange={(v) => setField('adults', v)} labelSize={isMobile ? 28 : 22} />
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontSize: formFz(12), mb: 1 }}>小孩</Typography>
            <Stepper value={form.children} onChange={(v) => setField('children', v)} labelSize={isMobile ? 28 : 22} />
          </Box>
        </Box>

        {/* T-023 桌號：自動配桌 / 自選桌號 */}
        <Box sx={{ borderTop: '1px solid #f0f0f0', pt: 2, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: formFz(11), color: '#999', mb: 0.25 }}>桌號</Typography>
            <Typography sx={{ fontSize: formFz(15), fontWeight: 700, color: selectedTables.length ? '#0f2b4a' : '#145da0' }}>
              {selectedTables.length === 0
                ? '自動配桌'
                : selectedTables.length === 1
                  ? `${selectedTables[0].tableNumber}桌`
                  : selectedTables.length >= 4
                    ? `併 ${selectedTables.length} 桌（共 ${selectedSeats} 座）`
                    : `併 ${selectedTables.map((t) => t.tableNumber).join('、')} 桌（共 ${selectedSeats} 座）`}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
            {selectedTables.length > 0 && (
              <Button size="small" onClick={pickAutoAssign}
                sx={{ fontSize: formFz(12), color: '#666', textTransform: 'none' }}>改回自動</Button>
            )}
            <Button size="small" variant="outlined" onClick={openTablePicker}
              sx={{ fontSize: formFz(12), textTransform: 'none', borderColor: '#145da0', color: '#145da0' }}>
              {selectedTables.length ? '更改桌號' : '自選桌號'}
            </Button>
          </Stack>
        </Box>

        {addonConfig.length > 0 && (
          <Box sx={{ borderTop: '1px solid #f0f0f0', pt: 2, mb: 2 }}>
            <Typography sx={{ fontSize: formFz(11), color: '#999', letterSpacing: 1, textTransform: 'uppercase', mb: 1.5 }}>
              附加項目
            </Typography>
            {addonConfig.map(cfg => (
              <Box key={cfg.key} sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: (cfg.type === 'single_select' || cfg.type === 'multi_select') ? 1 : 0 }}>
                  <Typography sx={{ fontSize: formFz(12) }}>{cfg.label || cfg.key}</Typography>
                  {(cfg.type === 'counter' || cfg.type === 'toggle') && renderAddonControl(cfg)}
                </Box>
                {(cfg.type === 'single_select' || cfg.type === 'multi_select') && renderAddonControl(cfg)}
              </Box>
            ))}
          </Box>
        )}

        <Box sx={{ borderTop: '1px solid #f0f0f0', pt: 2 }}>
          <Box sx={{ mb: 1.5 }}>
            <Typography sx={{ fontSize: formFz(11), color: '#666', mb: 0.5 }}>手機號碼</Typography>
            <TextField size="small" fullWidth placeholder="0912 345 678"
              type="tel"
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              sx={isMobile ? { '& .MuiInputBase-input': { fontSize: formFz(15) } } : undefined}
              value={form.customerPhone}
              onChange={(e) => setField('customerPhone', e.target.value)} />
          </Box>

          <Box sx={{ mb: 1.5 }}>
            <Typography sx={{ fontSize: formFz(11), color: '#666', mb: 0.5 }}>姓名 <span style={{ color: '#d32f2f' }}>*</span></Typography>
            <Stack direction="row" spacing={1}>
              <TextField size="small" fullWidth placeholder="姓名"
                sx={isMobile ? { '& .MuiInputBase-input': { fontSize: formFz(15) } } : undefined}
                value={form.customerName}
                onChange={(e) => setField('customerName', e.target.value)} />
              <Stack direction="row" sx={{ border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden', flexShrink: 0 }}>
                {['ms','mr'].map(g => (
                  <Box key={g} onClick={() => setField('customerGender', g)}
                    sx={{
                      px: 1.5, py: 1, fontSize: formFz(12), cursor: 'pointer', display: 'flex', alignItems: 'center',
                      bgcolor: form.customerGender === g ? '#145da0' : 'white',
                      color: form.customerGender === g ? 'white' : '#666',
                    }}>
                    {g === 'ms' ? '小姐' : '先生'}
                  </Box>
                ))}
              </Stack>
            </Stack>
          </Box>

          {/* 特殊需求（後台自訂標記，多選）— 放在姓名與備註之間 */}
          {specialReqTags.length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <Typography sx={{ fontSize: formFz(11), color: '#666', mb: 0.5 }}>特殊需求</Typography>
              {isMobile ? (
                <Select
                  multiple fullWidth size="small" displayEmpty
                  value={selectedSpecialReqs}
                  onChange={(e) => {
                    const v = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                    setSpecialReqs(v);
                  }}
                  renderValue={(sel) => (!sel || sel.length === 0)
                    ? <span style={{ color: '#999' }}>點此選擇（可多選）</span>
                    : sel.join('、')}
                  sx={{ '& .MuiSelect-select': { fontSize: formFz(14), py: 1, whiteSpace: 'normal' } }}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 340 } } }}
                >
                  {specialReqTags.map(tag => (
                    <MenuItem key={tag} value={tag} sx={{ py: 0.25 }}>
                      <Checkbox checked={selectedSpecialReqs.includes(tag)} size="small" sx={{ py: 0.25 }} />
                      <ListItemText primary={tag} primaryTypographyProps={{ fontSize: formFz(14) }} />
                    </MenuItem>
                  ))}
                </Select>
              ) : (
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                  {specialReqTags.map(tag => (
                    <AddonChip key={tag} label={tag} selected={selectedSpecialReqs.includes(tag)}
                      fontSize={formFz(14)} onClick={() => toggleSpecialReq(tag)} />
                  ))}
                </Stack>
              )}
            </Box>
          )}

          <Box>
            <Typography sx={{ fontSize: formFz(11), color: '#666', mb: 0.5 }}>備註</Typography>
            <TextField size="small" fullWidth multiline rows={2} placeholder="客人特別需求、過敏…"
              sx={isMobile ? { '& .MuiInputBase-input': { fontSize: formFz(15) } } : undefined}
              value={form.customerNote}
              onChange={(e) => setField('customerNote', e.target.value)} />
          </Box>
        </Box>
      </Box>

      <Box sx={{ p: 1.5, borderTop: '1px solid #e0e0e0', flexShrink: 0 }}>
        <Button
          fullWidth variant="contained" disabled={submitting} onClick={submitForm}
          sx={{
            bgcolor: '#145da0', '&:hover': { bgcolor: '#0e447a' },
            py: isMobile ? 1.5 : 1.25, fontWeight: 600, fontSize: formFz(14), textTransform: 'none',
          }}
        >
          {submitting
            ? (isEditing ? '更新中…' : '建立中…')
            : (isEditing
                ? '確認更新'
                : `確認新增${totalParty > 0 ? `（共 ${totalParty} 位）` : ''}`)
          }
        </Button>
        <Typography sx={{ fontSize: formFz(10), color: '#bbb', textAlign: 'center', mt: 0.75 }}>
          {isEditing ? '欄位變更後送出更新' : '系統自動配桌 — 無合適桌位列為候補'}
        </Typography>

        {/* 待回撥：回電之後不一定訂得成。收合起來，因為成交才是主要動作。 */}
        {pendingRequest && (
          <Box component="details" sx={{ mt: 1 }}>
            <Box component="summary" sx={{ cursor: 'pointer', fontSize: formFz(11), color: '#94a3b8', textAlign: 'center' }}>
              其他結果（沒訂成）
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75 }}>
              <Button size="small" fullWidth variant="outlined" onClick={() => resolvePending('no_answer')}
                sx={{ textTransform: 'none', fontSize: formFz(11), color: '#92400e', borderColor: '#fbbf24' }}>
                聯絡不上
              </Button>
              <Button size="small" fullWidth variant="outlined" onClick={() => resolvePending('no_deal')}
                sx={{ textTransform: 'none', fontSize: formFz(11), color: '#64748b', borderColor: '#cbd5e1' }}>
                未成交
              </Button>
              <Button size="small" fullWidth variant="outlined" onClick={() => resolvePending('ignored')}
                sx={{ textTransform: 'none', fontSize: formFz(11), color: '#64748b', borderColor: '#cbd5e1' }}>
                不需處理
              </Button>
            </Box>
            <Typography sx={{ fontSize: formFz(10), color: '#bbb', textAlign: 'center', mt: 0.5 }}>
              「聯絡不上」仍會留在待回撥清單
            </Typography>
          </Box>
        )}
      </Box>
    </>
  );

  // ──────── 手機卡片列表（桌機用 Table，手機用卡片；點卡片＝編輯 sheet）────────
  const renderMobileCards = () => {
    if (resLoading && reservations.length === 0) {
      return Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={`sk-${i}`} variant="rounded" height={84} sx={{ borderRadius: '14px', mb: 1 }} />
      ));
    }
    if (!resLoading && filteredReservations.length === 0) {
      return (
        <Box sx={{ py: 6, textAlign: 'center', color: '#999' }}>
          <RestaurantMenuIcon sx={{ fontSize: 48, color: '#ddd', mb: 1, display: 'block', mx: 'auto' }} />
          <Typography>{searchQuery ? '找不到符合搜尋的訂位' : (date === todayYmd() ? '今天沒有訂位記錄' : '此日期沒有訂位記錄')}</Typography>
        </Box>
      );
    }
    const fs = mobileFs; // 卡片基準字級（20/22/24，由全域「A」字級驅動）；其餘元素依此縮放
    return groupedRows.map(row => {
      if (row.type === 'divider') {
        return (
          <Box key={`h-${row.hour}`} sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, px: 0.5, pt: 1.5, pb: 0.5 }}>
            <Typography sx={{ fontSize: fs - 2, fontWeight: 800, color: '#334155' }}>{row.hour}:00</Typography>
            <Typography sx={{ fontSize: fs - 8, color: '#94a3b8' }}>共 {row.totalGuests} 位 · {row.count} 組</Typography>
          </Box>
        );
      }
      const r = row.reservation;
      const { adults, children } = partyBreakdown(r);
      const badges = renderExtraBadges(r.extras, addonConfig, fs - 8);
      const waitlist = !r.table?.tableNumber;
      const isHl = highlightId && String(r.id) === String(highlightId); // 119 從推播／新進訂單跳過來的那一筆
      return (
        <Paper key={r.id} onClick={() => openEditSheet(r)}
          ref={isHl ? highlightRowRef : undefined}
          sx={{ display: 'flex', gap: 1.5, alignItems: 'stretch', p: 1.75, mb: 1, borderRadius: '14px',
                boxShadow: '0 1px 3px rgba(15,43,74,0.08)', cursor: 'pointer',
                ...(isHl ? { bgcolor: HL_BG } : {}),
                borderLeft: `4px solid ${isHl ? HL_BAR : (waitlist ? '#ff9800' : '#145da0')}` }}>
          {/* col-l：時間 + 桌號 */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.75, minWidth: 56, flexShrink: 0 }}>
            <Typography sx={{ fontSize: fs, fontWeight: 800, color: '#0f2b4a', letterSpacing: '.5px' }}>{fmtTime(r.reservationTime)}</Typography>
            <Box title={tableFullLabel(r)} sx={{ fontSize: fs - 2, fontWeight: 800, px: 1.25, py: '2px', borderRadius: '8px', whiteSpace: 'nowrap',
                       bgcolor: waitlist ? '#fff3e0' : '#e8f0f9', color: waitlist ? '#e65100' : '#145da0' }}>
              {tableChipLabel(r)}
            </Box>
          </Box>
          {/* col-m：姓名+電話 / 來源 / 附加 */}
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.6 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: fs, fontWeight: 700 }}>{formatDisplayName(r.customerName, r.customerGender)}</Typography>
              <Typography component="span" sx={{ fontSize: fs, fontWeight: 600, color: '#334155' }}>{renderPhoneWithEmphasis(r.customerPhone, fs - 2)}</Typography>
            </Box>
            {SOURCE_LABEL[r.source] && (
              <Box component="span" sx={{ alignSelf: 'flex-start', fontSize: fs - 8, fontWeight: 700, px: 0.9, py: '2px', borderRadius: '5px', bgcolor: '#eceff1', color: '#546e7a' }}>
                {SOURCE_LABEL[r.source]}
              </Box>
            )}
            {badges}
          </Box>
          {/* col-r：人數（大/小）+ 狀態 */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'space-between', gap: 1, flexShrink: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '3px' }}>
              <Typography sx={{ fontSize: fs + 4, fontWeight: 800, color: '#0f2b4a', lineHeight: 1 }}>{adults}</Typography>
              <Typography sx={{ fontSize: fs - 6, color: '#6b7280', fontWeight: 600 }}>大</Typography>
              {children > 0 && (<>
                <Typography sx={{ fontSize: fs + 4, fontWeight: 800, color: '#0f2b4a', lineHeight: 1 }}>{children}</Typography>
                <Typography sx={{ fontSize: fs - 6, color: '#6b7280', fontWeight: 600 }}>小</Typography>
              </>)}
            </Box>
            <StatusDot status={r.status} size={fs - 4} />
          </Box>
          {/* ⋮：狀態變更 / 取消（mockup 未畫，但手機仍需保留這些動作）*/}
          <IconButton onClick={(e) => openRowMenu(e, r)} sx={{ alignSelf: 'center', width: 40, height: 40, color: '#90a4ae', flexShrink: 0 }}>
            <MoreVertIcon fontSize="medium" />
          </IconButton>
        </Paper>
      );
    });
  };

  return (
    <Layout disableContentZoom>
      <Box sx={{ m: -3, display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>

        {/* ═════════ Reservation pane (middle, flex:1) ═════════ */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>

          {/* Header */}
          <Box sx={{ flexShrink: 0, bgcolor: 'white', borderBottom: '1px solid #e0e0e0', px: { xs: 1.5, md: 3 }, pt: 2.5, pb: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: { xs: 'wrap', md: 'nowrap' }, gap: 1 }}>
              <Box sx={{ flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: 22, fontWeight: 700, whiteSpace: 'nowrap' }}>訂單管理</Typography>
                  <Tooltip title={socketConnected ? 'WebSocket 即時更新已連線' : 'WebSocket 未連線'}>
                    <Box component="span" sx={{
                      width: 10, height: 10,
                      bgcolor: socketConnected ? '#4caf50' : '#bdbdbd',
                      borderRadius: '50%', display: 'inline-block',
                    }} />
                  </Tooltip>
                </Box>
                <Typography sx={{ fontSize: 13, color: '#999' }}>管理當日內用訂位與外帶訂單</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: { xs: 1, md: 3 }, alignItems: 'flex-end', flexWrap: 'wrap', width: { xs: '100%', md: 'auto' }, justifyContent: { xs: 'space-between', md: 'flex-end' } }}>
                {renderTabStats()}
              </Box>
            </Box>

            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              sx={{
                minHeight: 40,
                '& .MuiTabs-indicator': { backgroundColor: '#145da0' },
                '& .MuiTab-root': {
                  minHeight: 40, py: 0, textTransform: 'none', fontSize: '14px',
                  fontWeight: 500, color: '#666',
                  '&.Mui-selected': { color: '#145da0', fontWeight: 600 },
                },
              }}
            >
              <Tab
                value={0}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <RestaurantMenuIcon sx={{ fontSize: 16 }} />
                    <span>今日訂位</span>
                    <Box component="span" sx={{
                      fontSize: 12, fontWeight: 600, px: 1, py: '1px', borderRadius: '10px',
                      bgcolor: tab === 0 ? '#e3f2fd' : '#eee',
                    }}>{stats.total}</Box>
                  </Box>
                }
              />
              {/* 待回撥訂位（feature_plans/115）— badge 比照外帶：有待處理就實心底白字。
                  用琥珀色而非藍色，因為這裡的「未處理」是會流失生意的，跟外帶的待核帳不同量級。 */}
              <Tab
                value={1}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <PhoneCallbackIcon sx={{ fontSize: 16 }} />
                    <span>待回撥訂位</span>
                    <Box component="span" sx={{
                      fontSize: 12, fontWeight: 600, px: 1, py: '1px', borderRadius: '10px',
                      bgcolor: pendingCount > 0 ? '#e65100' : (tab === 1 ? '#e3f2fd' : '#eee'),
                      color: pendingCount > 0 ? 'white' : 'inherit',
                    }}>{pendingCount}</Box>
                  </Box>
                }
              />
              <Tab
                value={2}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <TakeoutDiningIcon sx={{ fontSize: 16 }} />
                    <span>外帶訂單</span>
                    <Box component="span" sx={{
                      fontSize: 12, fontWeight: 600, px: 1, py: '1px', borderRadius: '10px',
                      bgcolor: takeoutActive > 0 ? '#145da0' : (tab === 2 ? '#e3f2fd' : '#eee'),
                      color: takeoutActive > 0 ? 'white' : 'inherit',
                    }}>{takeoutActive}</Box>
                  </Box>
                }
              />
            </Tabs>
          </Box>

          {/* 日期導覽 + 客滿狀態（今日訂位 tab）— 綁同一個日期，日期只顯示一次（不重複）。
              手機：日期導覽獨立一排（置中、放大箭頭），客滿控制項另一排。桌機：同一排、字級與導覽一致。 */}
          {tab === 0 && (
          <Box sx={{ flexShrink: 0 }}>
            {/* 日期 + 客滿狀態一排（日期只顯示一次）：[‹ 日期 ›] [開放中▾] [設定客滿] */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
              px: { xs: 1.5, md: 3 }, pt: 1.5 }}>
              <SingleDateNavigator value={date} onChange={(d) => { clearHighlight(); setDate(d); }} maxDate={FAR_FUTURE}
                compact={isMobile} showIcon={!isMobile}
                fontSize={headerFz(14)} arrowSize={headerFz(24)} buttonSize="small" />
              <DayStatusManager selectedDate={date} fz={headerFz} isMobile={isMobile} />
            </Box>

            {/* 搜尋 + 重整（放一起） */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1,
              px: { xs: 1.5, md: 3 }, py: 1.5, bgcolor: '#f9f9f9', borderBottom: '1px solid #f0f0f0' }}>
              <TextField
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="搜尋當日 姓名 / 電話 / 備註"
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#999' }} /></InputAdornment>,
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchQuery('')} edge="end">
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{ flex: 1, minWidth: 0, bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
              />
              <Tooltip title="重新整理">
                <IconButton onClick={() => fetchReservations(date)} disabled={resLoading} size="small"
                  sx={{ flexShrink: 0, borderRadius: '6px', bgcolor: 'rgba(20,93,160,0.06)', '&:hover': { bgcolor: 'rgba(20,93,160,0.14)' } }}>
                  <RefreshIcon fontSize="small" sx={{ color: '#145da0' }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          )}

          {/* 外帶訂單 tab（Plan 106）：action row＋列表＋右側明細抽屜 */}
          {tab === 1 && (
            <PendingCallbacksPanel
              refreshSignal={pendingRefresh}
              onCountsChange={setPendingCount}
              onSelect={selectPendingRequest}
              selectedId={pendingRequest?.id || null}
              isMobile={isMobile}
              mobileFs={mobileFs}
              headerSx={headerSx}
              cellSx={cellSx}
              fz={formFz}
              showSnack={(msg, sev) => showSnack(sev || 'info', msg)}
            />
          )}

          {tab === 2 && (
            <TakeoutOrdersPanel
              refreshSignal={takeoutRefresh}
              onCountsChange={setTakeoutCounts}
              showSnack={showSnack}
            />
          )}

          {/* Scrollable area（今日訂位 tab） */}
          {tab === 0 && (
          <Box sx={{
            flex: 1, overflow: 'auto', px: 3,
            '&::-webkit-scrollbar': { width: '6px' },
            '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.2)', borderRadius: '3px' },
          }}>
            {/* 119 highlight 提示帶。找得到就說標在哪；找不到也要講話，不要靜默什麼都不做。 */}
            {highlightId && (
              <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5,
                mt: 2, px: 1.5, py: 1.1, borderRadius: '6px',
                bgcolor: highlightMissing ? '#fff4e5' : HL_BG,
                border: `1px solid ${highlightMissing ? '#ffcc80' : '#ffe082'}`,
                color: highlightMissing ? '#8a5100' : '#8d6e00',
                fontSize: 13.5,
              }}>
                <Typography sx={{ fontSize: 'inherit', color: 'inherit' }}>
                  {highlightMissing
                    ? `這筆訂位已不在 ${fmtDateWithWeekday(date)}，可能已改期或取消`
                    : (() => {
                      const r = reservations.find((x) => String(x.id) === String(highlightId));
                      return r
                        ? `已為您標出「${formatDisplayName(r.customerName, r.customerGender)} ${fmtTime(r.reservationTime)}」這筆訂位`
                        : '已為您標出該筆訂位';
                    })()}
                </Typography>
                <Button size="small" onClick={clearHighlight} sx={{ color: 'inherit', textDecoration: 'underline', flexShrink: 0 }}>
                  {highlightMissing ? '知道了' : '取消標示'}
                </Button>
              </Box>
            )}
            {isMobile ? renderMobileCards() : (
              <TableContainer component={Paper} sx={{ mt: 2, boxShadow: '0 2px 4px rgba(0,0,0,0.06)', maxHeight: 'calc(100vh - 260px)', overflow: 'auto' }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headerSx}>時間</TableCell>
                      <TableCell sx={headerSx}>桌號</TableCell>
                      <TableCell sx={headerSx}>姓名</TableCell>
                      <TableCell sx={headerSx} align="right">人數</TableCell>
                      <TableCell sx={headerSx}>電話</TableCell>
                      <TableCell sx={headerSx}>備註 / 附加</TableCell>
                      <TableCell sx={headerSx}>狀態</TableCell>
                      <TableCell sx={headerSx} align="center">操作</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {resLoading && reservations.length === 0 && renderSkeletonRows(8)}
                    {!resLoading && filteredReservations.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 6, color: '#999' }}>
                          <RestaurantMenuIcon sx={{ fontSize: 48, color: '#ddd', mb: 1, display: 'block', mx: 'auto' }} />
                          <Typography>
                            {searchQuery
                              ? '找不到符合搜尋的訂位'
                              : (date === todayYmd() ? '今天沒有訂位記錄' : '此日期沒有訂位記錄')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {groupedRows.map(row => {
                      if (row.type === 'divider') {
                        return (
                          <TableRow key={`hour-${row.hour}`} sx={{ bgcolor: '#f5f5f5', '&:hover': { bgcolor: '#f5f5f5' } }}>
                            <TableCell colSpan={8} sx={{ py: 0.75, borderBottom: '1px solid #e0e0e0' }}>
                              <Stack direction="row" spacing={2} alignItems="center">
                                <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#333', minWidth: 56 }}>
                                  {row.hour}:00
                                </Typography>
                                <Typography sx={{ fontSize: 12, color: '#666' }}>
                                  共 {row.totalGuests} 位 · {row.count} 組
                                </Typography>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      }
                      const r = row.reservation;
                      const badges = renderExtraBadges(r.extras, addonConfig);
                      const isEditingThis = editingReservation && editingReservation.id === r.id;
                      // 119：從推播／新進訂單跳過來的那一筆。編輯藍底優先序高於黃底。
                      const isHl = highlightId && String(r.id) === String(highlightId);
                      return (
                        <TableRow
                          key={r.id}
                          ref={isHl ? highlightRowRef : undefined}
                          sx={{
                            ...rowSx,
                            ...(isHl ? rowHighlightSx : {}),
                            ...(isEditingThis ? rowEditingSx : {}),
                          }}
                          onClick={() => enterEditMode(r)}
                        >
                          <TableCell sx={cellSx}>{fmtTime(r.reservationTime)}</TableCell>
                          <TableCell sx={cellSx}>
                            {(r.table?.tableNumber || (Array.isArray(r.combinedTables) && r.combinedTables.length)) ? (
                              <Typography title={tableFullLabel(r)} sx={{ fontSize: cellFontSize, fontWeight: 500 }}>{tableChipLabel(r)}</Typography>
                            ) : (
                              <Typography sx={{ fontSize: cellFontSize - 1, color: '#ff9800', fontWeight: 500 }}>候補</Typography>
                            )}
                          </TableCell>
                          <TableCell sx={cellSx}>
                            {formatDisplayName(r.customerName, r.customerGender)}
                            <SourceChip source={r.source} />
                          </TableCell>
                          <TableCell sx={cellSx} align="right">
                            <Typography component="span" sx={{ fontSize: cellFontSize + 6, fontWeight: 700, lineHeight: 1 }}>
                              {r.partySize}
                            </Typography>
                          </TableCell>
                          <TableCell sx={cellSx}>{renderPhoneWithEmphasis(r.customerPhone, cellFontSize)}</TableCell>
                          <Tooltip title={r.customerNote || ''} placement="top" disableHoverListener={!r.customerNote}>
                            <TableCell sx={{ ...cellSx, maxWidth: 260 }}>
                              {badges}
                              {(r.customerNote || !badges) && (
                                <Typography sx={{
                                  fontSize: 12, color: r.customerNote ? '#666' : '#bbb', mt: badges ? 0.5 : 0,
                                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 240,
                                }}>
                                  {r.customerNote || '無附加項目'}
                                </Typography>
                              )}
                            </TableCell>
                          </Tooltip>
                          <TableCell sx={cellSx}><StatusDot status={r.status} /></TableCell>
                          <TableCell sx={cellSx} align="center">
                            <Tooltip title="更多操作">
                              <IconButton onClick={(e) => openRowMenu(e, r)}
                                sx={{ width: 40, height: 40, borderRadius: '8px', bgcolor: 'rgba(25,118,210,0.08)', '&:hover': { bgcolor: 'rgba(25,118,210,0.18)' } }}>
                                <MoreVertIcon fontSize="medium" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

          </Box>
          )}
        </Box>

        {/* ═════════ Form pane (right, 340px — 桌機；手機改用 bottom sheet) ═════════
             今日訂位＝常駐（要拿來新增）；待回撥＝點了某一筆才展開，關掉收回，列表拿回全寬。 */}
        {(tab === 0 || (tab === 1 && pendingRequest)) && !isMobile && (
        <Box sx={{
          width: 340, flexShrink: 0, bgcolor: 'white', borderLeft: '1px solid #e0e0e0',
          display: 'flex', flexDirection: 'column',
        }}>
          {renderFormPaneInner()}
        </Box>
        )}
      </Box>

      {/* ═════════ 手機：右下浮動「＋新增訂位」FAB + 由下往上 bottom sheet（今日訂位 tab）═════════ */}
      {isMobile && (tab === 0 || (tab === 1 && pendingRequest)) && (
        <>
          {tab === 0 && (
          <Fab
            aria-label="新增訂位"
            onClick={openNewSheet}
            sx={{
              position: 'fixed', right: 20, bottom: 24, zIndex: 1200,
              bgcolor: '#145da0', color: '#fff', '&:hover': { bgcolor: '#0e447a' },
            }}
          >
            <AddIcon />
          </Fab>
          )}
          <Drawer
            anchor="bottom"
            open={sheetOpen}
            onClose={handleFormClose}
            PaperProps={{ sx: {
              height: '88vh', borderTopLeftRadius: 18, borderTopRightRadius: 18,
              display: 'flex', flexDirection: 'column', overflow: 'hidden',
            } }}
          >
            <Box sx={{ width: 44, height: 5, borderRadius: 3, bgcolor: '#d5dbe3', mx: 'auto', mt: 1, mb: 0.5, flexShrink: 0 }} />
            {renderFormPaneInner()}
          </Drawer>
        </>
      )}

      {/* T-023 自選桌號 — 清單式 picker（bottom sheet；桌號｜可坐N人｜位置(有設樓層才顯示)｜適配；剛好排前標建議；已佔/不足灰掉）*/}
      <Drawer anchor="bottom" open={pickerOpen} onClose={() => setPickerOpen(false)}
        PaperProps={{ sx: { borderTopLeftRadius: 18, borderTopRightRadius: 18, maxHeight: '82vh', display: 'flex', flexDirection: 'column' } }}>
        <Box sx={{ width: 44, height: 5, borderRadius: 3, bgcolor: '#d5dbe3', mx: 'auto', mt: 1, mb: 0.5, flexShrink: 0 }} />
        <Box sx={{ px: 2, pt: 0.5, pb: 1.5, flexShrink: 0, borderBottom: '1px solid #eef1f5' }}>
          <Typography sx={{ fontSize: formFz(16), fontWeight: 800, color: '#0f2b4a' }}>選擇桌位（可併桌）</Typography>
          <Typography sx={{ fontSize: formFz(11), color: '#94a3b8', mt: 0.25 }}>
            {date} · {form.reservationTime} · 共 {totalParty} 位 — 可勾多張桌併桌，座位加總需 ≥ 人數
          </Typography>
          {selectedTables.length > 0 && (
            <Box sx={{ mt: 1, px: 1.25, py: 0.75, borderRadius: 1.5, display: 'flex', alignItems: 'center', gap: 1,
              bgcolor: seatsEnough ? 'rgba(6,199,85,.1)' : '#fef0f0', border: '1px solid', borderColor: seatsEnough ? 'rgba(6,199,85,.4)' : '#f5c2c7' }}>
              <Typography sx={{ fontSize: formFz(12.5), fontWeight: 700, color: seatsEnough ? '#2e7d32' : '#c62828' }}>
                已選 {selectedTables.length} 張 · 共 {selectedSeats} 座
              </Typography>
              <Typography sx={{ ml: 'auto', fontSize: formFz(13), fontWeight: 800, color: seatsEnough ? '#2e7d32' : '#c62828' }}>
                {seatsEnough ? `足夠（需 ${totalParty}）` : `還差 ${totalParty - selectedSeats} 位`}
              </Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ flex: 1, overflow: 'auto', p: 1.5 }}>
          {pickerLoading ? (
            <Box sx={{ py: 5, textAlign: 'center', color: '#999' }}>載入中…</Box>
          ) : (
            <>
              {/* 自動配桌 */}
              <Box onClick={pickAutoAssign}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, mb: 1, borderRadius: 2, cursor: 'pointer',
                      bgcolor: selectedTables.length === 0 ? '#f0f6fc' : '#fff', border: selectedTables.length === 0 ? '2px solid #145da0' : '1px solid #e0e0e0' }}>
                <Box>
                  <Typography sx={{ fontSize: formFz(15), fontWeight: 700, color: '#0f2b4a' }}>自動配桌</Typography>
                  <Typography sx={{ fontSize: formFz(11), color: '#6b7280' }}>系統挑最適合的空桌（單桌）</Typography>
                </Box>
                {selectedTables.length === 0 && <Typography sx={{ fontSize: formFz(12), fontWeight: 800, color: '#145da0' }}>目前</Typography>}
              </Box>
              {pickerRows.length === 0 && (
                <Box sx={{ py: 4, textAlign: 'center', color: '#999' }}>尚無桌位</Box>
              )}
              {pickerRows.map((t) => {
                const sel = selectedTables.some((x) => x.id === t.id);
                return (
                  <Box key={t.id} onClick={() => t.selectable && toggleTable(t)}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: 1.5, mb: 1, borderRadius: 2,
                          cursor: t.selectable ? 'pointer' : 'not-allowed', opacity: t.selectable ? 1 : 0.55,
                          bgcolor: sel ? '#f0f6fc' : '#fff',
                          border: sel ? '2px solid #145da0' : '1px solid #e0e0e0' }}>
                    {/* 桌號膠囊 */}
                    <Box sx={{ display: 'inline-flex', alignItems: 'baseline', gap: '1px', bgcolor: '#e8f0f9', color: '#145da0', borderRadius: 1.5, px: 1, py: 0.5, flexShrink: 0 }}>
                      <Typography component="span" sx={{ fontSize: formFz(18), fontWeight: 800, lineHeight: 1 }}>{t.tableNumber}</Typography>
                      <Typography component="span" sx={{ fontSize: formFz(12), fontWeight: 700 }}>桌</Typography>
                    </Box>
                    {/* 可坐 N 人（獨立欄，放大）*/}
                    <Box sx={{ flexShrink: 0, textAlign: 'center' }}>
                      <Typography sx={{ fontSize: formFz(10), color: '#94a3b8', lineHeight: 1 }}>可坐</Typography>
                      <Typography sx={{ fontSize: formFz(17), fontWeight: 800, color: '#0f2b4a', lineHeight: 1.15 }}>
                        {t.seats}<Box component="span" sx={{ fontSize: formFz(11), fontWeight: 600, color: '#6b7280' }}>人</Box>
                      </Typography>
                    </Box>
                    {/* 位置（僅在該桌有設定樓層/區域時才顯示，獨立欄）*/}
                    {t.floor && (
                      <Box sx={{ flexShrink: 0, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: formFz(10), color: '#94a3b8', lineHeight: 1 }}>位置</Typography>
                        <Typography sx={{ fontSize: formFz(14), fontWeight: 700, color: '#546e7a', lineHeight: 1.15 }}>{t.floor}</Typography>
                      </Box>
                    )}
                    {/* 適配提示 */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: formFz(14), fontWeight: 700, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        color: t.fit.tone === 'best' ? '#2e7d32' : (t.fit.tone === 'occ' ? '#c62828' : (t.fit.tone === 'closed' ? '#b06a00' : '#546e7a')) }}>
                        {t.fit.text}
                      </Typography>
                    </Box>
                    {/* 建議 + 選 */}
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0 }}>
                      {t.selectable && (
                        <Box sx={{ fontSize: formFz(14), fontWeight: 700, px: 1.75, py: 0.75, borderRadius: 1.5,
                          color: sel ? '#145da0' : '#fff', bgcolor: sel ? '#e8f0f9' : '#145da0' }}>{sel ? '已選' : '選'}</Box>
                      )}
                    </Stack>
                  </Box>
                );
              })}
            </>
          )}
        </Box>
        <Box sx={{ p: 1.5, borderTop: '1px solid #eef1f5', flexShrink: 0 }}>
          <Button fullWidth variant="contained" onClick={() => setPickerOpen(false)}
            sx={{ textTransform: 'none', bgcolor: '#145da0', fontWeight: 700, fontSize: formFz(14), py: 1, '&:hover': { bgcolor: '#0e447a' } }}>
            {selectedTables.length > 0 ? `完成（${selectedTables.length} 張 · 共 ${selectedSeats} 座）` : '完成'}
          </Button>
        </Box>
      </Drawer>

      {/* Quick-action menu (⋮): status changes + edit + cancel */}
      <Menu anchorEl={rowMenuAnchor} open={!!rowMenuAnchor} onClose={closeRowMenu}>
        <MenuItem onClick={openEditFromMenu}>編輯訂位</MenuItem>
        <MenuItem disabled sx={{ opacity: 0.6, fontSize: '0.8rem' }}>變更狀態</MenuItem>
        <MenuItem onClick={() => handleStatusChange('confirmed')}>已確認</MenuItem>
        <MenuItem onClick={() => handleStatusChange('seated')}>用餐中</MenuItem>
        <MenuItem onClick={() => handleStatusChange('no_show')}>未到</MenuItem>
        <MenuItem onClick={handleCancel} sx={{ color: 'error.main' }}>取消訂位</MenuItem>
      </Menu>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* P2 inc-2: CallTranscriptDrawer (通話逐字稿) removed — re-add with P3 voice. */}
    </Layout>
  );
};

export default RestaurantReservationsPage;
